<template>
  <MainLayout>
    <template #header>
      <div class="mb-6">
        <h1 class="text-2xl font-bold">Associates</h1>
        <p class="text-gray-600">Global directory of associates across all environments.</p>
      </div>
    </template>

    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex flex-wrap items-center gap-3 mb-4">
        <span class="p-input-icon-left flex-1 min-w-[260px]">
          <i class="pi pi-search" />
          <InputText
            v-model="search"
            placeholder="Search by name, email, or discipline"
            class="w-full"
          />
        </span>

        <Dropdown
          v-model="selectedOrgId"
          :options="orgOptions"
          optionLabel="name"
          optionValue="id"
          placeholder="All organisations"
          :filter="true"
          showClear
          class="min-w-[260px]"
          @change="loadAssociates"
        />

        <span class="text-sm text-gray-500">
          {{ filteredRows.length }} / {{ rows.length }}
        </span>
      </div>

      <AssociatesTable :rows="filteredRows" :loading="loading" />
    </div>
  </MainLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '../store/auth';
import MainLayout from '../components/MainLayout.vue';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import AssociatesTable from '../components/AssociatesTable.vue';

const toast = useToast();
const search = ref('');
const selectedOrgId = ref(null);
const organisations = ref([]);
const rows = ref([]);
const loading = ref(false);

const orgOptions = computed(() => organisations.value.map((o) => ({ id: o.id, name: o.name })));

const filteredRows = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return rows.value;
  return rows.value.filter((r) => {
    const name       = (r.name || '').toLowerCase();
    const email      = (r.email || '').toLowerCase();
    const discipline = (r.discipline || '').toLowerCase();
    const orgName    = (r.organisation_name || '').toLowerCase();
    return name.includes(term) || email.includes(term) ||
           discipline.includes(term) || orgName.includes(term);
  });
});

const loadOrganisations = async () => {
  try {
    const res = await axios.get('/api/v1/organisations');
    organisations.value = (res.data?.data || res.data || []);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load organisations', life: 3000 });
  }
};

const loadAssociates = async () => {
  loading.value = true;
  try {
    const params = { per_page: 500 };
    if (selectedOrgId.value) params.organisation_id = selectedOrgId.value;
    const res = await axios.get('/api/v1/associates', { params });
    rows.value = res.data?.data || [];
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load associates', life: 3000 });
    rows.value = [];
  } finally {
    loading.value = false;
  }
};

const authStore = useAuthStore();

onMounted(async () => {
  await loadOrganisations();
  await loadAssociates();
});

// Refetch when the exchange instance (core/tula) changes so the toggle takes
// effect without a full page reload. Reset the org filter since it is
// instance-specific.
watch(() => authStore.exchangeInstance, () => {
  selectedOrgId.value = null;
  loadOrganisations();
  loadAssociates();
});
</script>
